import { useCallback, useMemo, useRef } from 'react';
import i18n from '../i18n';
import { setGitConnectWorkspace, setGitConflict, setGitSecrets } from '../store/uiModalStore';
import { useGitSyncStore } from '../store/gitSyncStore';
import { useNotificationStore } from '../store/notificationStore';
import { trackLocally, saveVersion, addRemote, REMOTE_HAS_WORKSPACE } from '../services/git/localGitSync';
import { isLocalOnly, noteGitSync } from '../services/git/gitSyncStatus';
import { dequeue } from '../services/git/offlineQueue';
import { removeMirror } from '../services/workspaceMirror';
import { recordFeature } from '../services/telemetry';
import { GIT } from '../services/telemetry/features';

/** A title-bar notification (the corner toasts are off app-wide). */
function notify(kind, key, vars, titleKey) {
  useNotificationStore.getState()[kind]?.(i18n.t(key, vars), titleKey ? i18n.t(titleKey) : undefined);
}

/**
 * Track on this device (T3, T6, T7 — 04.10.2026): the Git page's handlers for
 * a workspace kept in a LOCAL git history. Called from useWorkspaces (which
 * routes local links here) so that file does not grow.
 *
 * - startTracking: "This device only" in the Connect dialog → first version.
 * - saveVersion: "Save version" (the local Sync now). Never queued, no network.
 * - addRemote: the Connect dialog in "Add a remote" mode → push the history.
 * - stopTracking: the link, the mirror (with its history) and the status go.
 *
 * The corner toasts are off app-wide (ui/Toast.js), so outcomes a user must
 * see — a refusal, "nothing new", a restarted history — also go to the
 * title-bar notifications, like useWorkspaces' notifyGit.
 *
 * @param {{ toastT, snapshotForGit, updateWorkspaceGitMeta, applyMirrorToWorkspace, setGitSyncingWorkspaceId }} deps
 */
export function useGitLocal({ toastT, snapshotForGit, updateWorkspaceGitMeta, applyMirrorToWorkspace, setGitSyncingWorkspaceId }) {
  // "Commit anyway" from the secret-scan modal re-runs the CURRENT handler.
  const trackRef = useRef(null);
  const saveRef = useRef(null);
  const addRemoteRef = useRef(null);

  const startTracking = useCallback(async (workspace, { allowSecrets = false } = {}) => {
    if (!workspace?.id) return null;
    setGitSyncingWorkspaceId(workspace.id);
    try {
      const result = await trackLocally(workspace, { snapshot: await snapshotForGit(workspace), allowSecrets });
      if (result.status === 'secrets') {
        // Nothing was committed or linked yet — the workspace stays untracked.
        setGitSecrets({
          workspace,
          findings: result.findings,
          commitAnyway: () => trackRef.current?.(workspace, { allowSecrets: true }),
        });
        return result;
      }
      updateWorkspaceGitMeta(workspace.id, result.git);
      noteGitSync({ ...workspace, git: result.git }, { ok: true });
      recordFeature(GIT.TRACK_LOCAL);
      toastT('toast.gitTrackingLocal', 'success', { name: workspace.name });
      setGitConnectWorkspace(null);
      return result;
    } catch (err) {
      toastT('toast.gitTrackFailed', 'error', { message: err?.message || '' });
      throw err;
    } finally {
      setGitSyncingWorkspaceId(null);
    }
  }, [snapshotForGit, updateWorkspaceGitMeta, toastT, setGitSyncingWorkspaceId]);
  trackRef.current = startTracking;

  const saveLocalVersion = useCallback(async (workspace, { allowSecrets = false, fromQueue = false } = {}) => {
    if (!workspace?.id) return null;
    // A local history has nothing to send: if an old queue entry names it, drop it.
    if (fromQueue) {
      dequeue(workspace.id);
      return null;
    }
    // The history was missing (T7): this save starts a fresh one — say so.
    const missing = useGitSyncStore.getState().byWorkspace[String(workspace.id)]?.status?.initialized === false;
    setGitSyncingWorkspaceId(workspace.id);
    try {
      const result = await saveVersion(workspace, await snapshotForGit(workspace), { allowSecrets });
      if (result.status === 'secrets') {
        setGitSecrets({
          workspace,
          findings: result.findings,
          commitAnyway: () => saveRef.current?.(workspace, { allowSecrets: true }),
        });
        return result;
      }
      // "Commit anyway" is remembered, so Add a remote can warn (T6).
      if (result.git !== workspace.git) updateWorkspaceGitMeta(workspace.id, result.git);
      noteGitSync({ ...workspace, git: result.git }, { ok: true });
      if (missing) {
        toastT('toast.gitHistoryRestarted', 'info', { name: workspace.name });
        notify('info', 'toast.gitHistoryRestarted', { name: workspace.name });
      } else if (result.commit) {
        toastT('toast.gitVersionSaved', 'success', { name: workspace.name });
      } else {
        toastT('toast.gitNothingToSave', 'info', { name: workspace.name });
        notify('info', 'toast.gitNothingToSave', { name: workspace.name });
      }
      return result;
    } catch (err) {
      noteGitSync(workspace, { error: err?.message });
      toastT('toast.gitSaveFailed', 'error', { message: err?.message || '' });
      return null;
    } finally {
      setGitSyncingWorkspaceId(null);
    }
  }, [snapshotForGit, updateWorkspaceGitMeta, toastT, setGitSyncingWorkspaceId]);
  saveRef.current = saveLocalVersion;

  const addRemoteToLocal = useCallback(async (workspace, options = {}) => {
    if (!isLocalOnly(workspace)) return null;
    setGitSyncingWorkspaceId(workspace.id);
    try {
      const result = await addRemote(workspace, { ...options, createIfMissing: true, snapshot: await snapshotForGit(workspace) });
      if (result.status === 'secrets') {
        setGitSecrets({
          workspace,
          findings: result.findings,
          commitAnyway: () => addRemoteRef.current?.(workspace, { ...options, allowSecrets: true }),
        });
        return result;
      }
      if (result.status === 'diverged') {
        // Nothing was linked: the history stays on this device.
        noteGitSync(workspace, { error: 'The remote moved on — try Add a remote again' });
        toastT('toast.gitAddRemoteFailed', 'error', { message: i18n.t('toast.gitRemoteMoved') });
        return result;
      }
      // ok or conflicts: the remote is attached — from now on a normal remote link.
      updateWorkspaceGitMeta(workspace.id, result.git);
      const linked = { ...workspace, git: result.git };
      if (result.status === 'conflicts') {
        setGitConflict({ workspace: linked, conflicts: result.conflicts || [] });
        setGitConnectWorkspace(null);
        return result;
      }
      if (result.merged) await applyMirrorToWorkspace(linked);
      noteGitSync(linked, { ok: true });
      recordFeature(GIT.ADD_REMOTE);
      toastT('toast.gitRemoteAdded', 'success', { name: workspace.name });
      setGitConnectWorkspace(null);
      return result;
    } catch (err) {
      if (err?.code === REMOTE_HAS_WORKSPACE) {
        toastT('toast.gitRemoteHasWorkspace', 'error');
        notify('error', 'toast.gitRemoteHasWorkspace', {}, 'toast.gitAddRemoteRefusedTitle');
      } else {
        toastT('toast.gitAddRemoteFailed', 'error', { message: err?.message || '' });
      }
      // The dialog shows the reason and stays open.
      throw err;
    } finally {
      setGitSyncingWorkspaceId(null);
    }
  }, [snapshotForGit, updateWorkspaceGitMeta, applyMirrorToWorkspace, toastT, setGitSyncingWorkspaceId]);
  addRemoteRef.current = addRemoteToLocal;

  const stopTracking = useCallback(async (workspace) => {
    if (!isLocalOnly(workspace)) return;
    updateWorkspaceGitMeta(workspace.id, null);
    await removeMirror(workspace.id);
    dequeue(workspace.id);
    useGitSyncStore.getState().forget(String(workspace.id));
    toastT('toast.gitStoppedTracking', 'info', { name: workspace.name });
  }, [updateWorkspaceGitMeta, toastT]);

  return useMemo(
    () => ({ startTracking, saveLocalVersion, addRemoteToLocal, stopTracking }),
    [startTracking, saveLocalVersion, addRemoteToLocal, stopTracking]
  );
}
